import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { TimeoutErrorHandler } from './timeoutErrorHandler';
import { TokenExpirationHandler } from './tokenExpirationHandler';

/**
 * Global error handler for MSAL-related errors across the application.
 * This provides a unified way to handle authentication errors from any service.
 */
export class GlobalErrorHandler {
  private static isGlobalHandlerRegistered = false;

  /**
   * Handles service-level errors and determines if they are authentication-related.
   * If they are, it will trigger the appropriate handler.
   * 
   * @param error - The error to handle
   * @returns Promise resolving to true if the error was auth-related and handled, false otherwise
   */
  static async handleServiceError(error: any): Promise<boolean> {
    if (!error) return false;

    try {
      // Convert error to string for pattern matching
      const errorString = error instanceof Error ? error.toString() : String(error);
      const errorMessage = errorString.toLowerCase();

      // Check for monitor_window_timeout errors specifically
      if (TimeoutErrorHandler.isTimeoutError(error)) {
        console.log('[GlobalErrorHandler] Detected monitor_window_timeout error in service call');
        await TimeoutErrorHandler.handleTimeoutError(error);
        return true;
      }

      // Check for token expiration errors
      if (TokenExpirationHandler.isTokenExpirationError(error)) {
        console.log('[GlobalErrorHandler] Detected token expiration in service call');
        await TokenExpirationHandler.handleTokenExpiration(error);
        return true;
      }

      // Check for specific MSAL error types
      if (
        error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError
      ) {
        console.log(`[GlobalErrorHandler] Detected MSAL error: ${errorString}`);

        // For InteractionRequiredAuthError, handle token expiration
        if (error instanceof InteractionRequiredAuthError) {
          await TokenExpirationHandler.handleTokenExpiration(error);
          return true;
        }

        // For BrowserAuthError, check if it's a timeout error
        if (error instanceof BrowserAuthError && TimeoutErrorHandler.isTimeoutError(error)) {
          await TimeoutErrorHandler.handleTimeoutError(error);
          return true;
        }
      }

      // Not an auth-related error or already handled
      return false;
    } catch (handlerError) {
      console.error('[GlobalErrorHandler] Error handling auth error:', handlerError);
      return false;
    }
  }

  /**
   * Utility method to wrap service calls with automatic error handling
   * 
   * @param serviceCall - Async function representing the service call
   * @returns Promise resolving to the result of the service call, or throws if unhandled error
   */
  static async withAuthErrorHandling<T>(serviceCall: () => Promise<T>): Promise<T> {
    try {
      return await serviceCall();
    } catch (error) {
      // If we handled the auth error, throw a more specific error
      const wasHandled = await this.handleServiceError(error);

      if (wasHandled) {
        throw new Error('Authentication error occurred and was handled. Please try again.');
      }

      // If not an auth error, re-throw the original error
      throw error;
    }
  }

  /**
   * Sets up global unhandled promise rejection handling
   */
  static setupGlobalHandlers(): void {
    if (this.isGlobalHandlerRegistered) {
      return;
    }

    this.isGlobalHandlerRegistered = true;

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', async (event) => {
      const error = event.reason;

      // Only try to handle if it looks like a potential auth error
      const errorStr = String(error).toLowerCase();
      if (
        errorStr.includes('auth') ||
        errorStr.includes('token') ||
        errorStr.includes('msal') ||
        errorStr.includes('timeout') ||
        error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError
      ) {
        console.warn('[AuthErrorLogger] Unhandled auth-related promise rejection:', {
          reason: error,
          timestamp: new Date().toISOString()
        });

        // Try to handle it with our auth error handlers
        const wasHandled = await this.handleServiceError(error);

        if (wasHandled) {
          // If we handled it, prevent the default browser behavior
          event.preventDefault();
        }
      }
    });
  }
}
